import GuidePage from "./GuidePage";

const chapters = [
  {
    title: "Warum die Einleitung entscheidet",
    body: (
      <>
        <p>
          In den ersten fünfzehn Sekunden entscheidet sich, ob der Raum bei Ihnen ist. Nicht durch
          rhetorische Tricks — sondern durch <strong>Konkretheit</strong>. Wer bei den Lebensdaten
          beginnt, verliert. Wer bei einem einzigen, präzisen Bild beginnt, gewinnt.
        </p>
        <p>
          Die Einleitung einer Trauerrede muss zwei Dinge leisten: Aufmerksamkeit sammeln, ohne
          aufzudringen — und den Ton setzen, in dem der Rest der Rede getragen werden soll.
        </p>
      </>
    ),
  },
  {
    title: "Was Sie vermeiden sollten",
    body: (
      <>
        <ul className="space-y-3 list-disc list-inside">
          <li><strong>„Wir haben uns hier versammelt, um…“</strong> — der Anlass ist allen klar. Sie verlieren zehn wertvolle Sekunden.</li>
          <li><strong>Vollständiger Name mit Geburts- und Sterbedatum.</strong> Das gehört in die Todesanzeige, nicht in den Einstieg.</li>
          <li><strong>„Es fällt mir schwer, die richtigen Worte zu finden.“</strong> Ist verständlich, aber ein schwacher Anfang.</li>
          <li><strong>Zitate von Prominenten oder aus Büchern.</strong> Klingt oft geliehen. Wenn Zitat, dann eines des Verstorbenen selbst.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Was funktioniert — vier Ansätze",
    body: (
      <>
        <p><strong>1. Das konkrete Bild.</strong></p>
        <p className="italic text-ink pl-4 border-l-2 border-umber/30">
          „Wenn ich an sie denke, denke ich an das Küchenfenster. Es war das erste, das morgens auf war.“
        </p>

        <p className="mt-6"><strong>2. Ein Satz, den er oder sie oft gesagt hat.</strong></p>
        <p className="italic text-ink pl-4 border-l-2 border-umber/30">
          „„Wir schauen mal“, sagte er, wenn etwas Schwieriges anstand. Und dann schauten wir. Und dann ging es meistens.“
        </p>

        <p className="mt-6"><strong>3. Ein winziger, sinnlicher Moment.</strong></p>
        <p className="italic text-ink pl-4 border-l-2 border-umber/30">
          „Der Duft von frischem Kaffee um sechs Uhr morgens — das war er, bevor er ein einziges Wort gesagt hatte.“
        </p>

        <p className="mt-6"><strong>4. Eine schlichte, ehrliche Feststellung.</strong></p>
        <p className="italic text-ink pl-4 border-l-2 border-umber/30">
          „Es gibt Menschen, deren Zimmer man nach einem Besuch aufgeräumter verlässt, als man es betreten hat. Sie war so ein Mensch.“
        </p>
      </>
    ),
  },
  {
    title: "Wie es weitergeht — nach den ersten Sätzen",
    body: (
      <>
        <p>
          Nach dem konkreten Einstieg brauchen Sie einen sanften Übergang zum Wer: Wer war dieser
          Mensch für uns, jenseits dieses einen Bildes? Der Übergang darf ganz klein sein:
        </p>
        <p className="italic text-ink pl-4 border-l-2 border-umber/30">
          „So haben wir sie erlebt — jeden Morgen. Und so möchte ich Ihnen kurz von ihr erzählen.“
        </p>
        <p>
          Von hier aus öffnen sich zwei bis drei prägende Züge, konkrete Beispiele, und ein leiser Schluss.
        </p>
      </>
    ),
  },
];

const faq = [
  {
    q: "Wie fängt man eine Trauerrede am besten an?",
    a: "Mit einem konkreten Bild — einer Geste, einem Satz, einem sinnlichen Moment, der nur zu dieser einen Person passt. Vermeiden Sie „Wir haben uns hier versammelt…“ — der Anlass ist allen bewusst.",
  },
  {
    q: "Ist es ok, mit einem Zitat zu beginnen?",
    a: "Nur, wenn es ein Zitat des Verstorbenen selbst ist. Fremdzitate klingen bei Trauerreden oft geliehen und schwächen den persönlichen Ton.",
  },
  {
    q: "Muss ich mich in der Einleitung selbst vorstellen?",
    a: "In den meisten Fällen nicht — die meisten im Raum kennen Sie oder wissen, wer Sie sind. Wenn Sie sich vorstellen, halten Sie es kurz („Ich bin Anna, die Tochter von…“) und tun Sie es nach dem konkreten Einstieg, nicht davor.",
  },
  {
    q: "Kann ich mit dem Todesdatum beginnen?",
    a: "Besser nicht. Das Datum ist Verwaltung, kein Anfang. Datum und Lebensspanne können später oder gar nicht erwähnt werden — sie stehen ohnehin in der Todesanzeige und im Programmheft.",
  },
];

const related = [
  { label: "Trauerrede schreiben — Anleitung", to: "/trauerrede-schreiben" },
  { label: "Wie lang darf sie sein?", to: "/wie-lang-trauerrede" },
  { label: "Eine Trauerrede halten", to: "/trauerrede-halten" },
];

export default function TrauerredeEinleitung() {
  return (
    <GuidePage
      slug="trauerrede-einleitung"
      metaTitle="Trauerrede-Einleitung — der erste Satz, der trägt | Oratiorede"
      metaDescription="Die Einleitung einer Trauerrede entscheidet in 15 Sekunden. Vier funktionierende Ansätze — und was Sie vermeiden sollten. Praktische Beispiele."
      eyebrow="Ratgeber · Einleitung"
      headline={
        <>
          Die Einleitung einer Trauerrede.
          <br />
          <span className="italic text-umber">Der erste Satz, der trägt.</span>
        </>
      }
      intro="In den ersten fünfzehn Sekunden entscheidet sich, ob der Raum bei Ihnen ist. Vier ehrliche Ansätze für einen ersten Satz, der trägt — und vier häufige Fehler, die Sie vermeiden sollten."
      chapters={chapters}
      faq={faq}
      related={related}
    />
  );
}
